/**
 * Use Case Templates Hook (DC-1077)
 *
 * React hook for fetching and managing use case templates and assessments.
 * Provides API integration for template CRUD and assessment operations.
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  UseCaseTemplate,
  UseCaseTemplateSummary,
  UseCaseAssessment,
  TemplateListFilters,
  TemplateListResponse,
  TemplateDetailResponse,
  AssessmentResponse,
  IntakeValidationResponse,
  IntakeAnswerValue,
} from './types';

// ============================================================================
// API BASE URL
// ============================================================================

const API_BASE = '/api/v1/use-case-templates';

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for fetching list of templates
 */
export function useTemplateList(
  organizationId?: string,
  filters?: TemplateListFilters
): {
  templates: UseCaseTemplateSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [templates, setTemplates] = useState<UseCaseTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (organizationId) params.set('org_id', organizationId);
      if (filters?.domain) params.set('domain', filters.domain);
      if (filters?.category) params.set('category', filters.category);
      if (filters?.risk_level) params.set('risk_level', filters.risk_level);
      if (filters?.include_system !== undefined) {
        params.set('include_system', String(filters.include_system));
      }

      const url = `${API_BASE}?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch templates');
      }

      const data: TemplateListResponse = await response.json();
      setTemplates(data.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
      console.error('[useTemplateList] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters?.domain, filters?.category, filters?.risk_level, filters?.include_system]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refresh: fetchTemplates };
}

/**
 * Hook for fetching a single template
 */
export function useTemplate(
  idOrSlug: string | null,
  organizationId?: string
): {
  template: UseCaseTemplate | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [template, setTemplate] = useState<UseCaseTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplate = useCallback(async () => {
    if (!idOrSlug) {
      setTemplate(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (organizationId) params.set('org_id', organizationId);

      const url = `${API_BASE}/${idOrSlug}?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          setTemplate(null);
          return;
        }
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch template');
      }

      const data: TemplateDetailResponse = await response.json();
      setTemplate(data.template);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
      console.error('[useTemplate] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [idOrSlug, organizationId]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { template, loading, error, refresh: fetchTemplate };
}

/**
 * Hook for fetching journey assessment
 */
export function useJourneyAssessment(
  journeyId: string | null
): {
  assessment: UseCaseAssessment | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [assessment, setAssessment] = useState<UseCaseAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessment = useCallback(async () => {
    if (!journeyId) {
      setAssessment(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = `/api/v1/journeys/${journeyId}/template-assessment`;
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          setAssessment(null);
          return;
        }
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch assessment');
      }

      const data: AssessmentResponse = await response.json();
      setAssessment(data.assessment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assessment');
      console.error('[useJourneyAssessment] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  return { assessment, loading, error, refresh: fetchAssessment };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Validate intake answers against a template
 */
export async function validateIntakeAnswers(
  templateId: string,
  answers: Record<string, IntakeAnswerValue>
): Promise<IntakeValidationResponse> {
  const response = await fetch(`${API_BASE}/${templateId}/validate-intake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to validate intake answers');
  }

  return data;
}

/**
 * Start an assessment for a journey
 */
export async function startAssessment(
  journeyId: string,
  templateId: string,
  intakeAnswers: Record<string, IntakeAnswerValue>,
  organizationId?: string
): Promise<UseCaseAssessment> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (organizationId) {
    headers['X-Org-Id'] = organizationId;
  }

  const response = await fetch(`/api/v1/journeys/${journeyId}/template-assessment`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      template_id: templateId,
      intake_answers: intakeAnswers,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to start assessment');
  }

  return data.assessment;
}

/**
 * Calculate assessment score
 */
export async function calculateAssessmentScore(
  journeyId: string
): Promise<UseCaseAssessment> {
  const response = await fetch(`/api/v1/journeys/${journeyId}/template-assessment/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to calculate assessment score');
  }

  return data.assessment;
}

/**
 * Create a custom template
 */
export async function createTemplate(
  input: Partial<UseCaseTemplate>,
  organizationId: string
): Promise<UseCaseTemplate> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Org-Id': organizationId,
    },
    body: JSON.stringify(input),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create template');
  }

  return data.template;
}

/**
 * Update a template
 */
export async function updateTemplate(
  id: string,
  input: Partial<UseCaseTemplate>,
  organizationId: string
): Promise<UseCaseTemplate> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Org-Id': organizationId,
    },
    body: JSON.stringify(input),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update template');
  }

  return data.template;
}

/**
 * Delete a template
 */
export async function deleteTemplate(
  id: string,
  organizationId: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    headers: {
      'X-Org-Id': organizationId,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete template');
  }
}
